import { formatCurrency } from "@solomon-apps/shared";
import type { WalletParticipantAccount, YaasPositionCardData, YaasWalletStats } from "@solomon-apps/shared";
import { BigDecimal } from "@solomon-labs/bigdecimal";
import { useMemo } from "react";

import { retailMinBalance } from "@/config/retailYaasPolicy";
import type { NextDistribution, useYaasWalletData } from "@/hooks/yaas";

import type { useYaasRetailFlow } from "../flow/useYaasRetailFlow";
import type { DemoState } from "../helpers/demoState";
import { computeLandingVariant, type LandingVariant } from "../helpers/landingVariant";
import {
    computeQualifyingDistributionDate,
    DISTRIBUTION_CYCLE_DAYS,
    MS_PER_DAY
} from "../helpers/qualifyingDistribution";
import {
    MOCK_ACTIVE_POSITION,
    MOCK_BELOW_MIN_POSITION,
    MOCK_DISCONNECTED_POSITION,
    MOCK_GOLD_LP_WALLET_STATS,
    MOCK_GOLD_LP_WALLET_WALLET_STATS,
    MOCK_GOLD_RAYDIUM_LP_POSITION,
    MOCK_GOLD_WALLET_POSITION,
    MOCK_MIN_BALANCE,
    MOCK_MIN_YIELD_PER_DISTRIBUTION,
    MOCK_NEXT_DISTRIBUTION,
    MOCK_OPTED_IN_POSITION,
    MOCK_OPTED_IN_WALLET_STATS,
    MOCK_PENDING_POSITION,
    MOCK_WALLET_STATS
} from "../mocks/retailMocks";

import type { ResolvedView } from "./types";

const usdFmt = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
});

export interface UseResolvedRetailViewInput {
    flow: ReturnType<typeof useYaasRetailFlow>;
    walletAddress: string;
    demoState: DemoState;
    wallet: ReturnType<typeof useYaasWalletData>;
    overlaidWalletPositions: YaasPositionCardData[];
    overlaidWalletStats: YaasWalletStats | null;
    /** Exact Helius USDv, independent of the backend holdings response. */
    walletBalanceExact: BigDecimal | null;
    qualifyingBalance: BigDecimal | null;
    stablesBalance: number;
    isBalancesLoading: boolean;
    /** The Program's schedule, the same one the issuer console's
     *  Next Payout reads. Demo and sim views keep their fixture dates. */
    nextDistribution: NextDistribution;
    /** A Squads multisig is selected. It acquires USDv in Squads, not the
     *  in-app swap, so an empty vault shows the below-minimum card too. */
    isSquads?: boolean;
}

export function resolveRetailViewKind(
    account: WalletParticipantAccount | null,
    positionsCount: number,
    enrolling: boolean
): ResolvedView["viewKind"] {
    if (enrolling) return "enrolling";
    if (account && account.status !== "Active") return "account-unavailable";
    if (positionsCount > 0) return "positions";
    if (account?.status === "Active") return "rewards-enabled";
    return "not-enrolled";
}

/**
 * Resolves the full page-view object the right-column action card and
 * left-column hero render against. Encodes the 4-priority resolution
 * tree (sim → flow interactive → static demo → real default) documented
 * in `useResolvedRetailView.ANATOMY.md`.
 */
export function useResolvedRetailView({
    flow,
    walletAddress,
    demoState,
    wallet,
    overlaidWalletPositions,
    overlaidWalletStats,
    walletBalanceExact,
    qualifyingBalance,
    stablesBalance,
    isBalancesLoading,
    nextDistribution,
    isSquads = false
}: UseResolvedRetailViewInput): ResolvedView {
    return useMemo(() => {
        const isRealView = !flow.isSimulated && demoState === null;
        const fallbackAddr = walletAddress || "EnViUgEjqL6HzLRxLDnxTb4t5xukumJYpNwq6fxF1tJYY";
        const minBalanceExact =
            flow.isSimulated || demoState !== null
                ? new BigDecimal(String(MOCK_MIN_BALANCE))
                : retailMinBalance(wallet.minBalance);

        const base: ResolvedView = {
            viewKind: resolveRetailViewKind(
                wallet.account,
                overlaidWalletPositions.length,
                flow.pageView === "enrolling"
            ),
            account: wallet.account,
            walletStats: null,
            positions: [],
            isWalletConnected: flow.isWalletConnected,
            address: fallbackAddr,
            earliestPositionCreatedAt: null,
            nextDistributionDate: isRealView ? nextDistribution.at : null,
            distributionPeriodStartsAt: isRealView ? nextDistribution.periodStartsAt : null,
            // Use the temporary frontend policy when the API has not supplied
            // a threshold. Explicit demo/sim views keep fixed fixture values.
            minBalance: minBalanceExact.toNumber(),
            minBalanceExact,
            disabledByUser: false,
            pendingYield: null,
            projectedNextDistributionDate: null,
            isPreviewMode: false,
            previewPosition: null,
            previewBalanceLabel: "Available Positions",
            previewPrimaryLabel: "Start Earning",
            previewPrimaryArrow: true,
            previewShowGetMoreUsdv: true,
            // When true, the preview's primary CTA fires the
            // buy-USDv action instead of starting the apply flow.
            // Used by the apply-below-min demo where the user needs
            // to top up before they can usefully enroll.
            previewPrimaryGoesToBuy: false,
            // Connected-no-yaas variant. Defaults to "projection" so
            // every other branch keeps its existing rendering. Only the
            // connected-no-yaas branch and the matching demo states
            // override it.
            landingVariant: "projection",
            // Sum of liquid USDC+USDT in the wallet — feeds the
            // HeroSection's "swap-to-usdv" headline ("Your $X could
            // earn…") when landingVariant === "swap-from-stables".
            // Otherwise unused.
            stablesBalance: 0
        };

        // 1. Sim mode — derive view from flow.pageView.
        if (flow.isSimulated) {
            if (flow.pageView === "disconnected") {
                return {
                    ...base,
                    isWalletConnected: false,
                    address: "",
                    isPreviewMode: true,
                    previewPosition: MOCK_DISCONNECTED_POSITION,
                    previewBalanceLabel: "Holding",
                    previewPrimaryLabel: "See How Much You Can Earn",
                    previewPrimaryArrow: true,
                    previewShowGetMoreUsdv: false
                };
            }
            if (flow.pageView === "not-enrolled") {
                return { ...base, isPreviewMode: true, previewPosition: MOCK_ACTIVE_POSITION };
            }
            if (flow.pageView === "enrolling") {
                return { ...base, viewKind: "enrolling" };
            }
        }

        // 2. The enrollment form takes priority over kickoff demos. This makes
        //    Start Earning and Submit work
        //    when launched from a static `?state=connected-no-yaas`
        //    frame — without this block, those static frames would
        //    "freeze" the view and clicking the form would have no
        //    visible effect.
        //
        //    Terminal demo states (active / just-distributed / below-min /
        //    disabled / opted-in / pending) intentionally win over priority 2 —
        //    the user navigated to a specific demo destination, the
        //    URL is authoritative, and flow.pageView from a previous
        //    in-page click shouldn't override the requested view. The
        //    click-through behavior only matters from "kickoff" demos
        //    (connected-no-yaas / apply-below-min / disconnected /
        //    no demo) where the user actually progresses INTO an
        //    interactive flow.
        const isTerminalDemo =
            demoState === "active" ||
            demoState === "just-distributed" ||
            demoState === "below-min" ||
            demoState === "disabled" ||
            demoState === "opted-in" ||
            demoState === "pending" ||
            demoState === "gold-lp" ||
            demoState === "gold-lp-wallet";
        if (!isTerminalDemo) {
            // Demo-progressed interactive states ("kickoff" demos that
            // the user clicked through) need mocks because the demo's
            // wallet isn't connected to a real account; real-mode
            // interactive states need the actual wallet dashboard.
            // `demoState` is the URL `?state=...` value — when null,
            // the user is in real mode.
            if (flow.pageView === "enrolling") {
                return { ...base, viewKind: "enrolling" };
            }
        }

        // 3. Static demo frames — frozen views for design review.
        //    Each demo explicitly sets `isWalletConnected` so the
        //    resolution doesn't depend on the real wallet adapter's
        //    settle timing — without that, the value can flip from
        //    false → true on a re-render and the motion.div key
        //    flips with it (causing AnimatePresence to swap and
        //    render both copies briefly).
        if (demoState === "active") {
            // Small-balance variant of MOCK_ACTIVE_POSITION so the
            // qualifying-distribution projection actually fires in
            // the demo. With $500 at 10.5% APY, cycle accrual ≈
            // $1.01/wk — barely above the $1 threshold, so the user
            // gets pushed forward one cycle past the upcoming
            // distribution before they qualify (today + 12d).
            const activePosition: YaasPositionCardData = {
                ...MOCK_ACTIVE_POSITION,
                position: {
                    ...MOCK_ACTIVE_POSITION.position,
                    USDvValue: 500,
                    formattedUSDvValue: "$500.00"
                }
            };
            const pendingYield = 0.42;
            return {
                ...base,
                isWalletConnected: true,
                walletStats: MOCK_WALLET_STATS,
                positions: [activePosition],
                earliestPositionCreatedAt: "2026-01-15",
                nextDistributionDate: MOCK_NEXT_DISTRIBUTION,
                projectedNextDistributionDate: computeQualifyingDistributionDate({
                    pendingYield,
                    USDvValue: activePosition.position.USDvValue,
                    yaasApy: activePosition.position.yaasApy,
                    minYieldPerDistribution: MOCK_MIN_YIELD_PER_DISTRIBUTION,
                    nextDistributionDate: MOCK_NEXT_DISTRIBUTION
                }),
                pendingYield
            };
        }
        if (demoState === "gold-lp-wallet") {
            return {
                ...base,
                isWalletConnected: true,
                walletStats: MOCK_GOLD_LP_WALLET_WALLET_STATS,
                positions: [MOCK_GOLD_RAYDIUM_LP_POSITION, MOCK_GOLD_WALLET_POSITION],
                earliestPositionCreatedAt: "2026-01-15",
                nextDistributionDate: MOCK_NEXT_DISTRIBUTION,
                projectedNextDistributionDate: MOCK_NEXT_DISTRIBUTION,
                pendingYield: 0
            };
        }
        if (demoState === "gold-lp") {
            return {
                ...base,
                isWalletConnected: true,
                walletStats: MOCK_GOLD_LP_WALLET_STATS,
                positions: [MOCK_GOLD_RAYDIUM_LP_POSITION],
                earliestPositionCreatedAt: "2026-01-15",
                nextDistributionDate: MOCK_NEXT_DISTRIBUTION,
                projectedNextDistributionDate: MOCK_NEXT_DISTRIBUTION,
                pendingYield: 0
            };
        }
        if (demoState === "pending") {
            return {
                ...base,
                isWalletConnected: true,
                walletStats: MOCK_WALLET_STATS,
                positions: [MOCK_PENDING_POSITION],
                earliestPositionCreatedAt: "2026-01-15"
            };
        }
        if (demoState === "opted-in") {
            // Day zero of an opt-in Program: the position is Earning,
            // but nothing has accrued and no distribution has landed.
            // Position created today so "earning since" reads as new
            // rather than backdating the enrollment six months.
            return {
                ...base,
                isWalletConnected: true,
                walletStats: MOCK_OPTED_IN_WALLET_STATS,
                positions: [MOCK_OPTED_IN_POSITION],
                earliestPositionCreatedAt: new Date().toISOString(),
                nextDistributionDate: MOCK_NEXT_DISTRIBUTION,
                projectedNextDistributionDate: MOCK_NEXT_DISTRIBUTION,
                pendingYield: 0
            };
        }
        if (demoState === "below-min") {
            return {
                ...base,
                isWalletConnected: true,
                walletStats: MOCK_WALLET_STATS,
                positions: [MOCK_BELOW_MIN_POSITION],
                earliestPositionCreatedAt: "2026-01-15",
                pendingYield: 0
            };
        }
        if (demoState === "just-distributed") {
            // The user just received a distribution — pendingYield
            // resets to 0, balance untouched, and the cycle just
            // reset so the next distribution is a full DISTRIBUTION_
            // CYCLE_DAYS (7) out (not the shared MOCK_NEXT_DISTRIBUTION
            // 5-day-out target most other states use). Progress bar
            // reads near-empty as a result.
            const freshNextDistribution = new Date(Date.now() + DISTRIBUTION_CYCLE_DAYS * MS_PER_DAY).toISOString();
            return {
                ...base,
                isWalletConnected: true,
                walletStats: MOCK_WALLET_STATS,
                positions: [MOCK_ACTIVE_POSITION],
                earliestPositionCreatedAt: "2026-01-15",
                nextDistributionDate: freshNextDistribution,
                pendingYield: 0
            };
        }
        if (demoState === "disabled") {
            // User-disabled position — same layout as the Earning
            // card, but the pill flips to "Disabled" (error-base) and
            // the next-distribution countdown collapses to "—" since
            // the position isn't accruing. Manage Position remains
            // available for the user to re-enable.
            return {
                ...base,
                isWalletConnected: true,
                walletStats: MOCK_WALLET_STATS,
                positions: [MOCK_ACTIVE_POSITION],
                earliestPositionCreatedAt: "2026-01-15",
                disabledByUser: true
            };
        }
        if (demoState === "connected-no-yaas") {
            // Backwards-compatible alias: maps to the "has USDv"
            // variant (State 3) so existing screenshots/links keep
            // showing the projection card. Use the three explicit
            // `connected-*-only` / `connected-no-balance` demos to
            // exercise States 1, 2, 3 in isolation.
            return {
                ...base,
                isWalletConnected: true,
                isPreviewMode: true,
                previewPosition: MOCK_ACTIVE_POSITION
            };
        }
        if (demoState === "connected-no-balance") {
            return {
                ...base,
                isWalletConnected: true,
                isPreviewMode: true,
                landingVariant: "swap-no-stables"
            };
        }
        if (demoState === "connected-stables-only") {
            return {
                ...base,
                isWalletConnected: true,
                isPreviewMode: true,
                landingVariant: "swap-from-stables",
                stablesBalance: 500
            };
        }
        if (demoState === "connected-usdv-only") {
            // State 3 demo — small real-looking USDv balance so the
            // projection reads as a real user's holding, not the
            // $12,450.75 marketing number.
            const demoUsdv = 250;
            return {
                ...base,
                isWalletConnected: true,
                isPreviewMode: true,
                landingVariant: "projection",
                previewPosition: {
                    ...MOCK_ACTIVE_POSITION,
                    position: {
                        ...MOCK_ACTIVE_POSITION.position,
                        USDvValue: demoUsdv,
                        formattedUSDvValue: formatCurrency(demoUsdv)
                    }
                }
            };
        }
        if (demoState === "apply-below-min") {
            // Wallet connected, no position, balance below the
            // minimum required to enroll. The ghost-card preview
            // shows the minimum amount as the headline balance (so
            // the user reads "this is what you'd need"), and the
            // single primary CTA points at Get More USDv. Earnings
            // projection still renders against the minimum so the
            // user can see what enrolling at the floor would yield.
            const minPosition: YaasPositionCardData = {
                ...MOCK_ACTIVE_POSITION,
                position: {
                    ...MOCK_ACTIVE_POSITION.position,
                    USDvValue: MOCK_MIN_BALANCE,
                    formattedUSDvValue: usdFmt.format(MOCK_MIN_BALANCE)
                }
            };
            return {
                ...base,
                isWalletConnected: true,
                isPreviewMode: true,
                previewPosition: minPosition,
                previewBalanceLabel: "Holding",
                previewPrimaryLabel: "Get More USDv to Earn",
                previewPrimaryArrow: false,
                previewShowGetMoreUsdv: false,
                previewPrimaryGoesToBuy: true,
                landingVariant: "below-min"
            };
        }
        if (demoState === "disconnected") {
            return {
                ...base,
                isWalletConnected: false,
                address: "",
                isPreviewMode: true,
                previewPosition: MOCK_DISCONNECTED_POSITION,
                previewBalanceLabel: "Holding",
                previewPrimaryLabel: "See How Much You Can Earn",
                previewPrimaryArrow: true,
                previewShowGetMoreUsdv: false
            };
        }

        // 4. Real default — flow.pageView drives only the connection
        //    states here (interactive states already returned above).
        //    Wallet stats / positions / earliest-position date now
        //    come from useYaasWalletData (Phase 5 — the retail
        //    wallet endpoint. A Pending item here is a real Program Position.
        if (flow.pageView === "disconnected") {
            return {
                ...base,
                isWalletConnected: false,
                address: "",
                isPreviewMode: true,
                previewPosition: MOCK_DISCONNECTED_POSITION,
                previewBalanceLabel: "Holding",
                previewPrimaryLabel: "See How Much You Can Earn",
                previewPrimaryArrow: true,
                previewShowGetMoreUsdv: false
            };
        }
        if (
            flow.pageView === "not-enrolled" ||
            flow.pageView === "account-unavailable" ||
            flow.pageView === "rewards-enabled"
        ) {
            if (wallet.account) {
                return {
                    ...base,
                    viewKind: resolveRetailViewKind(wallet.account, overlaidWalletPositions.length, false),
                    walletStats: overlaidWalletStats,
                    positions: overlaidWalletPositions,
                    earliestPositionCreatedAt: wallet.earliestPositionCreatedAt
                };
            }
            // A returning wallet can have real positions before this local
            // flow runs. Show those positions instead of the preview.
            if (overlaidWalletPositions.length > 0) {
                return {
                    ...base,
                    walletStats: overlaidWalletStats,
                    positions: overlaidWalletPositions,
                    earliestPositionCreatedAt: wallet.earliestPositionCreatedAt
                };
            }
            const previewAmount = qualifyingBalance ?? walletBalanceExact;
            const previewPosition =
                previewAmount === null
                    ? null
                    : {
                          position: {
                              USDvValue: previewAmount.toNumber(),
                              usdvAmountExact: previewAmount.toString(),
                              formattedUSDvValue: formatCurrency(previewAmount)
                          }
                      };
            if (qualifyingBalance === null && walletBalanceExact !== null) {
                return {
                    ...base,
                    isPreviewMode: true,
                    landingVariant: "projection",
                    previewPosition,
                    previewBalanceLabel: "Wallet USDv",
                    previewPrimaryLabel: "Checking Positions",
                    previewPrimaryDisabled: true,
                    previewPrimaryArrow: false
                };
            }
            const variant: LandingVariant = computeLandingVariant({
                usdvBalance: qualifyingBalance?.toNumber() ?? 0,
                stablesBalance,
                isBalancesLoading,
                hasLoadedBalances: qualifyingBalance !== null
            });
            // Below the minimum, enrolling earns nothing, so the card offers
            // a top-up. Keep the combined holding and projection in the
            // preview; the minimum is only the eligibility threshold.
            // A null combined balance guards pending and failed reads.
            // A multisig has no in-app swap, so with no USDv (a swap variant)
            // it gets the same card at $0.00 and the Squads trade link.
            const belowMinVariant =
                variant === "projection" ||
                (isSquads && (variant === "swap-no-stables" || variant === "swap-from-stables"));
            if (belowMinVariant && qualifyingBalance?.lt(minBalanceExact)) {
                return {
                    ...base,
                    isWalletConnected: true,
                    isPreviewMode: true,
                    landingVariant: "below-min",
                    stablesBalance,
                    previewPosition,
                    previewBalanceLabel: "Wallet USDv",
                    previewPrimaryLabel: "Get More USDv to Earn",
                    previewPrimaryArrow: false,
                    previewShowGetMoreUsdv: false,
                    previewPrimaryGoesToBuy: true
                };
            }
            // Combined USDv is enough to project earnings. Do not invent
            // an enrolled position, rewards, or a balance while fetching it.
            return {
                ...base,
                isPreviewMode: true,
                landingVariant: variant,
                stablesBalance,
                previewBalanceLabel: "Wallet USDv",
                previewPosition: variant === "projection" ? previewPosition : null
            };
        }
        // Enrollment keeps the form open. It does not create position data.
        return {
            ...base,
            viewKind: "enrolling",
            walletStats: overlaidWalletStats,
            positions: overlaidWalletPositions,
            earliestPositionCreatedAt: wallet.earliestPositionCreatedAt
        };
    }, [
        flow.isSimulated,
        flow.pageView,
        flow.isWalletConnected,
        walletAddress,
        demoState,
        wallet,
        overlaidWalletPositions,
        overlaidWalletStats,
        walletBalanceExact,
        qualifyingBalance,
        stablesBalance,
        isBalancesLoading,
        nextDistribution,
        isSquads
    ]);
}
